import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, Search, Copy, Check, FileText, Pill, Award, Sparkles, Loader2, ChevronRight, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  nota: { label: 'Nota Clinica', color: '#2A9FCB', bg: '#EFF6FF', icon: FileText },
  terapia: { label: 'Terapia', color: '#059669', bg: '#ECFDF5', icon: Pill },
  prescrizione: { label: 'Prescrizione', color: '#D97706', bg: '#FFFBEB', icon: Award },
};

export default function ImportTemplateModal({ isOpen, onClose, onImport, targetField = 'diagnosi', initialSearch = '' }) {
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [loading, setLoading] = useState(false);
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      fetchTemplates();
      setSearchQuery(initialSearch);
      if (initialSearch) {
        suggestWithAI(initialSearch);
      }
    }
  }, [isOpen, initialSearch]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/api/clinical-templates/categories`, { headers });
      setCategories(res.data.categories);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchTemplates = async (category, type) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (type) params.append('type', type);
      const res = await axios.get(`${API}/api/clinical-templates?${params}`, { headers });
      setTemplates(res.data.templates);
    } catch (err) {
      console.error('Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const suggestWithAI = async (text) => {
    if (!text || text.length < 3) return;
    try {
      setAiSuggesting(true);
      const res = await axios.post(`${API}/api/clinical-templates/suggest`, {
        query: text,
        target_field: targetField
      }, { headers });
      setAiSuggestions(res.data.suggested_ids || []);
    } catch (err) {
      console.error('AI suggestion error:', err);
    } finally {
      setAiSuggesting(false);
    }
  };

  const selectCategory = (catName) => {
    const newCat = selectedCategory === catName ? null : catName;
    setSelectedCategory(newCat);
    setPreviewTemplate(null);
    fetchTemplates(newCat, selectedType);
  };

  const selectType = (type) => {
    const newType = selectedType === type ? null : type;
    setSelectedType(newType);
    setPreviewTemplate(null);
    fetchTemplates(selectedCategory, newType);
  };

  const handleImport = (template) => {
    onImport(template.content);
    toast.success(`Template "${template.title}" importato`);
    onClose();
  };

  const debounceSearch = useCallback(
    (() => {
      let timer;
      return (val) => {
        clearTimeout(timer);
        timer = setTimeout(() => suggestWithAI(val), 600);
      };
    })(),
    [targetField]
  );

  const handleSearchChange = (val) => {
    setSearchQuery(val);
    debounceSearch(val);
  };

  const filteredTemplates = templates.filter(t =>
    !searchQuery ||
    t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.subcategory.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort: AI suggestions first
  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    const aIdx = aiSuggestions.indexOf(a.id);
    const bIdx = aiSuggestions.indexOf(b.id);
    if (aIdx >= 0 && bIdx >= 0) return aIdx - bIdx;
    if (aIdx >= 0) return -1;
    if (bIdx >= 0) return 1;
    return 0;
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" data-testid="import-template-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[95vw] max-w-5xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gradient-to-r from-[#daeef9] to-[#f0f7fc]">
          <div className="flex items-center gap-3">
            <BookOpen className="w-5 h-5" style={{ color: '#2A9FCB' }} />
            <div>
              <h2 className="text-lg font-bold" style={{ color: '#2A9FCB' }}>Importa Template</h2>
              <p className="text-xs text-[#2A9FCB]/60">
                {targetField === 'diagnosi' ? 'Importa nota clinica nel campo diagnosi' :
                 targetField === 'terapia' ? 'Importa terapia prescritta' :
                 'Importa template nel campo selezionato'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg" data-testid="close-import-modal">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Type Filters + Search */}
        <div className="p-3 border-b border-gray-100 flex flex-wrap items-center gap-2">
          <div className="flex gap-1.5">
            {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
              const Icon = cfg.icon;
              return (
                <button
                  key={key}
                  onClick={() => selectType(key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    selectedType === key ? 'ring-2 ring-offset-1 scale-105' : 'opacity-70 hover:opacity-100'
                  }`}
                  style={{ background: cfg.bg, color: cfg.color, ringColor: cfg.color }}
                  data-testid={`filter-type-${key}`}
                >
                  <Icon className="w-3.5 h-3.5" /> {cfg.label}
                </button>
              );
            })}
          </div>
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => handleSearchChange(e.target.value)}
              placeholder="Cerca o descrivi i sintomi per suggerimenti AI..."
              className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]/30"
              data-testid="import-search"
              autoFocus
            />
            {aiSuggesting && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs text-[#2A9FCB]">
                <Loader2 className="w-3 h-3 animate-spin" /> AI
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 flex overflow-hidden">
          {/* Categories Sidebar */}
          <div className="w-48 border-r border-gray-100 overflow-y-auto shrink-0">
            <div className="p-2">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 px-2 mb-1">Categorie</p>
              {categories.map(cat => (
                <button
                  key={cat.name}
                  onClick={() => selectCategory(cat.name)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all ${
                    selectedCategory === cat.name
                      ? 'bg-[#2A9FCB]/10 text-[#2A9FCB] font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span className="truncate block">{cat.name}</span>
                  <span className="text-[10px] text-gray-400">{cat.count} template</span>
                </button>
              ))}
            </div>
          </div>

          {/* Template List */}
          <div className="flex-1 overflow-y-auto p-3">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-[#2A9FCB]" />
              </div>
            ) : previewTemplate ? (
              /* Preview */
              <div data-testid="import-preview">
                <button onClick={() => setPreviewTemplate(null)} className="text-sm text-[#2A9FCB] hover:underline flex items-center gap-1 mb-3">
                  <ChevronRight className="w-4 h-4 rotate-180" /> Torna alla lista
                </button>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-gray-900">{previewTemplate.title}</h3>
                      <p className="text-xs text-gray-400 mt-0.5">{previewTemplate.category} &rsaquo; {previewTemplate.subcategory}</p>
                    </div>
                    <button
                      onClick={() => handleImport(previewTemplate)}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-[#2A9FCB] text-white hover:bg-[#2590b8] transition-all"
                      data-testid="confirm-import-btn"
                    >
                      <Copy className="w-4 h-4" /> Importa nel campo
                    </button>
                  </div>
                  <div className="p-5 bg-gray-50">
                    <pre className="whitespace-pre-wrap text-sm text-gray-800 leading-relaxed font-sans">{previewTemplate.content}</pre>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {aiSuggestions.length > 0 && (
                  <div className="mb-3 flex items-center gap-2 text-xs text-[#2A9FCB]">
                    <Sparkles className="w-3.5 h-3.5" />
                    <span className="font-semibold">Suggeriti dall'IA in base alla tua ricerca</span>
                  </div>
                )}
                <div className="space-y-2">
                  {sortedTemplates.length === 0 ? (
                    <div className="text-center py-8 text-gray-400 text-sm">Nessun template trovato</div>
                  ) : (
                    sortedTemplates.map(template => {
                      const cfg = TYPE_CONFIG[template.type] || TYPE_CONFIG.nota;
                      const Icon = cfg.icon;
                      const isAiSuggested = aiSuggestions.includes(template.id);
                      return (
                        <div
                          key={template.id}
                          className={`rounded-xl border p-3 cursor-pointer transition-all group hover:shadow-sm ${
                            isAiSuggested ? 'border-[#2A9FCB]/40 bg-[#2A9FCB]/5' : 'border-gray-200 hover:border-[#2A9FCB]/30'
                          }`}
                          onClick={() => setPreviewTemplate(template)}
                          data-testid={`import-item-${template.id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                {isAiSuggested && <Sparkles className="w-3 h-3 text-[#2A9FCB]" />}
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: cfg.bg, color: cfg.color }}>
                                  <Icon className="w-3 h-3" /> {cfg.label}
                                </span>
                                <span className="text-[10px] text-gray-400">{template.category} &rsaquo; {template.subcategory}</span>
                              </div>
                              <h4 className="font-semibold text-gray-900 text-sm group-hover:text-[#2A9FCB]">{template.title}</h4>
                              <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{template.content.substring(0, 100)}...</p>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleImport(template); }}
                              className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#2A9FCB] text-white opacity-0 group-hover:opacity-100 transition-all hover:bg-[#2590b8]"
                              data-testid={`quick-import-${template.id}`}
                            >
                              Importa
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
